
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, TrendingDown, Users, DollarSign, Target } from "lucide-react";
import { Progress } from "@/components/ui/progress";

export default function ConversionMetrics({ leads, deals, contacts }) {
  // Lead Conversion Metrics
  const totalLeads = leads.length;
  const convertedLeads = leads.filter(l => l.status === 'converted').length;
  const leadConversionRate = totalLeads > 0 ? ((convertedLeads / totalLeads) * 100).toFixed(1) : 0;

  // Lead Status Breakdown
  const leadsByStatus = {
    new: leads.filter(l => l.status === 'new').length,
    contacted: leads.filter(l => l.status === 'contacted').length,
    qualifying: leads.filter(l => l.status === 'qualifying').length,
    converted: convertedLeads,
    lost: leads.filter(l => l.status === 'lost').length
  };

  // Deal Conversion Metrics - Fixed funnel logic
  const totalDeals = deals.length;
  const closedWonDeals = deals.filter(d => d.stage === 'closed_won').length;
  const closedLostDeals = deals.filter(d => d.stage === 'closed_lost').length;
  
  // Win rate should be calculated from closed deals only
  const closedDeals = closedWonDeals + closedLostDeals;
  const dealWinRate = closedDeals > 0 
    ? ((closedWonDeals / closedDeals) * 100).toFixed(1) 
    : 0;

  // Deal Stage Breakdown - show progression through stages
  const dealsByStage = {
    prospecting: deals.filter(d => d.stage === 'prospecting').length,
    qualification: deals.filter(d => d.stage === 'qualification').length,
    proposal: deals.filter(d => d.stage === 'proposal').length,
    negotiation: deals.filter(d => d.stage === 'negotiation').length,
    closed_won: closedWonDeals,
    closed_lost: closedLostDeals
  };

  // Revenue Metrics
  const totalRevenue = deals
    .filter(d => d.stage === 'closed_won')
    .reduce((sum, d) => sum + (d.total_value || 0), 0);

  const pipelineValue = deals
    .filter(d => !['closed_won', 'closed_lost'].includes(d.stage))
    .reduce((sum, d) => sum + (d.total_value || 0), 0);

  const avgDealSize = closedWonDeals > 0 ? (totalRevenue / closedWonDeals) : 0;

  // NEW: Consolidated Lead-to-Won Conversion
  // Count leads that resulted in closed won deals
  const wonDealsFromLeads = deals.filter(d => d.stage === 'closed_won' && d.lead_id).length;
  const leadToWonRate = totalLeads > 0 ? ((wonDealsFromLeads / totalLeads) * 100).toFixed(1) : 0;

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="border-border bg-card hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Lead Conversion</p>
              <Users className="w-5 h-5 text-primary" />
            </div>
            <div className="flex items-baseline gap-2">
              <p className="text-3xl font-bold text-foreground">{leadConversionRate}%</p>
              <p className="text-sm text-muted-foreground">
                {convertedLeads}/{totalLeads}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border bg-card hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Deal Win Rate</p>
              <Target className="w-5 h-5 text-primary" />
            </div>
            <div className="flex items-baseline gap-2">
              <p className="text-3xl font-bold text-foreground">{dealWinRate}%</p>
              <p className="text-sm text-muted-foreground">
                {closedWonDeals}/{closedDeals}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* NEW: Lead to Won Conversion */}
        <Card className="border-primary/20 bg-primary/5 hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-primary font-semibold">Lead → Won</p>
              <TrendingUp className="w-5 h-5 text-primary" />
            </div>
            <div className="flex items-baseline gap-2">
              <p className="text-3xl font-bold text-foreground">{leadToWonRate}%</p>
              <p className="text-sm text-primary">
                {wonDealsFromLeads}/{totalLeads}
              </p>
            </div>
            <p className="text-xs text-primary mt-2">End-to-end conversion</p>
          </CardContent>
        </Card>

        <Card className="border-border bg-card hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Avg Deal Size</p>
              <DollarSign className="w-5 h-5 text-primary" />
            </div>
            <p className="text-3xl font-bold text-foreground">
              ${avgDealSize.toLocaleString(undefined, { minimumFractionDigits: 0 })}
            </p>
            <p className="text-xs text-muted-foreground mt-2">{closedWonDeals} deals closed</p>
          </CardContent>
        </Card>

        <Card className="border-border bg-card hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Pipeline Value</p>
              <TrendingUp className="w-5 h-5 text-primary" />
            </div>
            <p className="text-3xl font-bold text-foreground">
              ${pipelineValue.toLocaleString(undefined, { minimumFractionDigits: 0 })}
            </p>
            <p className="text-xs text-muted-foreground mt-2">{totalDeals - closedDeals} active deals</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Lead Conversion Funnel */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Lead Conversion Funnel
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-slate-700">New Leads</span>
                <span className="text-sm font-bold text-slate-900">{leadsByStatus.new}</span>
              </div>
              <Progress value={(leadsByStatus.new / totalLeads) * 100} className="h-2" />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-slate-700">Contacted</span>
                <span className="text-sm font-bold text-slate-900">{leadsByStatus.contacted}</span>
              </div>
              <Progress value={(leadsByStatus.contacted / totalLeads) * 100} className="h-2 [&>div]:bg-purple-600" />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-slate-700">Qualificando</span>
                <span className="text-sm font-bold text-slate-900">{leadsByStatus.qualifying}</span>
              </div>
              <Progress value={(leadsByStatus.qualifying / totalLeads) * 100} className="h-2 [&>div]:bg-amber-600" />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-slate-700">Convertido</span>
                <span className="text-sm font-bold text-emerald-700">{leadsByStatus.converted}</span>
              </div>
              <Progress value={(leadsByStatus.converted / totalLeads) * 100} className="h-2 [&>div]:bg-emerald-600" />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-slate-700">Perdido</span>
                <span className="text-sm font-bold text-rose-700">{leadsByStatus.lost}</span>
              </div>
              <Progress value={(leadsByStatus.lost / totalLeads) * 100} className="h-2 [&>div]:bg-rose-600" />
            </div>

            <div className="pt-4 border-t border-slate-200 flex justify-between items-center">
              <span className="text-sm font-semibold text-slate-900">Taxa de Conversão</span>
              <span className="text-2xl font-bold text-emerald-600">{leadConversionRate}%</span>
            </div>
          </CardContent>
        </Card>

        {/* Deal Conversion Funnel - Fixed */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-600" />
              Funil de Conversão de Negócios
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Active Pipeline Stages */}
            <div className="space-y-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Pipeline Ativo</p>
              
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-slate-700">Prospecção</span>
                  <span className="text-sm font-bold text-slate-900">{dealsByStage.prospecting}</span>
                </div>
                <Progress 
                  value={totalDeals > 0 ? (dealsByStage.prospecting / totalDeals) * 100 : 0} 
                  className="h-2 [&>div]:bg-slate-600" 
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-slate-700">Qualificação</span>
                  <span className="text-sm font-bold text-slate-900">{dealsByStage.qualification}</span>
                </div>
                <Progress 
                  value={totalDeals > 0 ? (dealsByStage.qualification / totalDeals) * 100 : 0} 
                  className="h-2 [&>div]:bg-blue-600" 
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-slate-700">Proposta</span>
                  <span className="text-sm font-bold text-slate-900">{dealsByStage.proposal}</span>
                </div>
                <Progress 
                  value={totalDeals > 0 ? (dealsByStage.proposal / totalDeals) * 100 : 0} 
                  className="h-2 [&>div]:bg-purple-600" 
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-slate-700">Negociação</span>
                  <span className="text-sm font-bold text-slate-900">{dealsByStage.negotiation}</span>
                </div>
                <Progress 
                  value={totalDeals > 0 ? (dealsByStage.negotiation / totalDeals) * 100 : 0} 
                  className="h-2 [&>div]:bg-amber-600" 
                />
              </div>
            </div>

            {/* Closed Deals Section */}
            <div className="pt-3 border-t border-slate-200 space-y-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Negócios Fechados</p>
              
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-slate-700">Ganhos</span>
                  <span className="text-sm font-bold text-emerald-700">{dealsByStage.closed_won}</span>
                </div>
                <Progress 
                  value={closedDeals > 0 ? (dealsByStage.closed_won / closedDeals) * 100 : 0} 
                  className="h-2 [&>div]:bg-emerald-600" 
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-slate-700">Perdidos</span>
                  <span className="text-sm font-bold text-rose-700">{dealsByStage.closed_lost}</span>
                </div>
                <Progress 
                  value={closedDeals > 0 ? (dealsByStage.closed_lost / closedDeals) * 100 : 0} 
                  className="h-2 [&>div]:bg-rose-600" 
                />
              </div>
            </div>

            <div className="pt-4 border-t border-slate-200 grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-500 mb-1">Win Rate</p>
                <p className="text-xl font-bold text-emerald-600">{dealWinRate}%</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Avg Deal Size</p>
                <p className="text-xl font-bold text-slate-900">
                  ${avgDealSize.toLocaleString(undefined, { minimumFractionDigits: 0 })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
